// Lazy loading helper for heavy libraries
class LazyLibraryLoader {
  constructor() {
    this.cache = new Map();
    this.loading = new Map();
  }

  // Load specific asset file (with predefined paths to avoid webpack warning)
  async loadAssetFile(fileName) {
    const cacheKey = `asset-${fileName}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.loading.has(cacheKey)) {
      return this.loading.get(cacheKey);
    }

    let loadPromise;

    // Predefined paths to avoid webpack warning
    switch (fileName) {
      case 'buttons.html5.min':
        loadPromise = import('@/assets/backend_js/js/buttons.html5.min');
        break;
      case 'buttons.flash.min':
        loadPromise = import('@/assets/backend_js/js/buttons.flash.min');
        break;
      case 'buttons.print.min':
        loadPromise = import('@/assets/backend_js/js/buttons.print.min');
        break;
      case 'jszip.min':
        loadPromise = import('@/assets/backend_js/js/jszip.min');
        break;
      case 'pdfmake.min':
        loadPromise = import('@/assets/backend_js/js/pdfmake.min');
        break;
      case 'vfs_fonts':
        loadPromise = import('@/assets/backend_js/js/vfs_fonts');
        break;
      case 'natural.js':
        loadPromise = import('@/assets/backend_js/js/natural.js');
        break;
      default:
        throw new Error(`Unknown asset file: ${fileName}`);
    }

    const finalPromise = loadPromise.then(module => {
      const result = module.default || module;
      this.cache.set(cacheKey, result);
      this.loading.delete(cacheKey);
      return result;
    });

    this.loading.set(cacheKey, finalPromise);
    return finalPromise;
  }

  // Generic library loader with caching and loading state
  async loadLibrary(libraryName, importFunction, extractFunction = module => module.default) {
    if (this.cache.has(libraryName)) {
      return this.cache.get(libraryName);
    }

    if (this.loading.has(libraryName)) {
      return this.loading.get(libraryName);
    }

    const loadPromise = importFunction().then(module => {
      const result = extractFunction(module);
      this.cache.set(libraryName, result);
      this.loading.delete(libraryName);
      return result;
    }).catch(error => {
      this.loading.delete(libraryName);
      console.error(`Error loading library ${libraryName}:`, error);
      throw error;
    });

    this.loading.set(libraryName, loadPromise);
    return loadPromise;
  }

  // Lazy load jsPDF (named export since v2+)
  async loadJsPDF() {
    return this.loadLibrary(
      'jspdf',
      () => import('jspdf'),
      module => module.jsPDF || module.default,
    );
  }

  // Lazy load html2canvas
  async loadHtml2Canvas() {
    return this.loadLibrary('html2canvas', () => import('html2canvas'));
  }

  // Lazy load fabric.js (Vue 2 stack keeps fabric 5.x; mitigate SVG XSS export)
  async loadFabric() {
    return this.loadLibrary('fabric', () => import('fabric'), (module) => {
      const fabric = module.fabric;
      this.applyFabricSvgXssMitigation(fabric);
      return fabric;
    });
  }

  /**
   * Fabric 5.x has stored XSS via SVG serialization (GHSA-hfvx-25r5-qc3w / GHSA-w22m-hvvm-xmwx).
   * App only uses PNG toDataURL today; still harden toSVG / toSVGElement if called.
   */
  applyFabricSvgXssMitigation(fabric) {
    if (!fabric || fabric.__pdraiSvgXssPatched) {
      return;
    }
    fabric.__pdraiSvgXssPatched = true;

    const stripDangerousSvg = (svg) => String(svg || '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<foreignObject[\s\S]*?<\/foreignObject>/gi, '')
      .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/javascript\s*:/gi, '');

    const patchProto = (proto, methodName) => {
      if (!proto || typeof proto[methodName] !== 'function') {
        return;
      }
      const original = proto[methodName];
      proto[methodName] = function patchedSvgExport(...args) {
        const result = original.apply(this, args);
        return typeof result === 'string' ? stripDangerousSvg(result) : result;
      };
    };

    if (fabric.Canvas && fabric.Canvas.prototype) {
      patchProto(fabric.Canvas.prototype, 'toSVG');
    }
    if (fabric.Object && fabric.Object.prototype) {
      patchProto(fabric.Object.prototype, 'toSVG');
    }
  }

  // Lazy load Swiper bundle (>=12.1.2 CVE-patched; vanilla API, Vue 2 compatible)
  async loadSwiper() {
    return this.loadLibrary(
      'swiper',
      () => import('swiper/bundle'),
      module => module.default || module.Swiper || module,
    );
  }

  // Get cache size
  getCacheSize() {
    return this.cache.size;
  }

  // Get cache info
  getCacheInfo() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      loading: Array.from(this.loading.keys())
    };
  }

  // Clear cache for specific library
  clearCache(libraryName) {
    this.cache.delete(libraryName);
  }

  // Clear all cache
  clearAllCache() {
    this.cache.clear();
    this.loading.clear();
  }
}

// Create singleton instance
const lazyLoader = new LazyLibraryLoader();

export default lazyLoader;
